/**
 * Общие типы HTTP-слоя: то, чем в Dart были `RequestOptions`, `Response`,
 * `Options` и `Interceptor` из dio.
 *
 * Dio из порта убран (в браузере он не нужен и тянет за собой io), вместо
 * него самописный клиент поверх `fetch`. Воспроизводится только та часть
 * поверхности dio, которой приложение действительно пользуется: `get`,
 * `post`, `queryParameters`, `headers`, таймауты и цепочка перехватчиков.
 *
 * Главное отличие от dio — неизменяемость. В Dart перехватчик правил
 * `RequestOptions` на месте (`options.extra[...] = ...`) либо звал
 * `copyWith`; здесь каждый перехватчик возвращает новый объект запроса.
 */

import type { HttpError, HttpErrorType } from '@/utils/exceptions/appException';

/** Методы, которыми ходит приложение. Ничего другого dio-версия не звала. */
export type HttpMethod = 'GET' | 'POST';

/** Таймауты в миллисекундах — `BaseOptions`/`Options` из dio. */
export interface HttpTimeouts {
  /** Ожидание ответа сервера (`connectTimeout`). */
  readonly connectTimeout: number;
  /** Чтение тела ответа (`receiveTimeout`). */
  readonly receiveTimeout: number;
  /** Отправка тела запроса (`sendTimeout`). */
  readonly sendTimeout: number;
}

/**
 * Значение параметра query. Приложение кладёт туда только числа и строки
 * (`offset`, `limit`, `refresh_token`), список оставлен на случай повторов.
 */
export type HttpQueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly (string | number | boolean)[];

export type HttpQueryParameters = Readonly<Record<string, HttpQueryValue>>;

/** Аргументы `client.get` — то же, что именованные параметры dio. */
export interface HttpRequestOptions {
  readonly queryParameters?: HttpQueryParameters;
  /** `Options(headers: …)`; `null` — как в Dart, «заголовков нет». */
  readonly headers?: Readonly<Record<string, string>> | null;
  readonly connectTimeout?: number;
  readonly receiveTimeout?: number;
  readonly sendTimeout?: number;
}

/** Аргументы `client.post`: те же плюс тело. */
export interface HttpPostOptions extends HttpRequestOptions {
  /**
   * Тело запроса: обычный объект (уйдёт JSON-ом) либо `FormData`
   * (multipart-загрузка wav для распознавания речи).
   */
  readonly data?: unknown;
}

/**
 * Запрос в том виде, в каком его видят перехватчики, — аналог
 * `RequestOptions`. Меняется только через новые объекты.
 */
export interface HttpRequest {
  readonly method: HttpMethod;
  /**
   * Адрес запроса. До `PathInterceptor` — с плейсхолдерами
   * (`${baseUrl}`, `${lang}`), после него — настоящий абсолютный адрес.
   */
  readonly path: string;
  readonly queryParameters: HttpQueryParameters;
  readonly headers: Readonly<Record<string, string>>;
  readonly data: unknown;
  readonly timeouts: HttpTimeouts;
  /** `RequestOptions.extra`: перехватчики кладут сюда свои отметки. */
  readonly extra: Readonly<Record<string, unknown>>;
}

/** Ответ — аналог `Response<dynamic>` из dio. */
export interface HttpResponse {
  /** Разобранный JSON либо текст тела. */
  readonly data: unknown;
  readonly statusCode: number;
  readonly headers: Headers;
  /**
   * Настоящий адрес запроса (`options.uri`): с query, но без заворачивания
   * в прокси — в журнал и в сообщения об ошибках идёт именно он.
   */
  readonly requestUrl: string;
  /** Запрос после всех перехватчиков — `response.requestOptions`. */
  readonly request: HttpRequest;
}

/**
 * Перехватчик — порт `InterceptorsWrapper`.
 *
 * `onError` получает запрос отдельным аргументом: в dio он лежал внутри
 * `DioException.requestOptions`, а форма ошибки [HttpError] фиксирована
 * слоем исключений и запроса не содержит.
 */
export interface HttpInterceptor {
  readonly onRequest?: (request: HttpRequest) => HttpRequest;
  readonly onResponse?: (response: HttpResponse) => HttpResponse;
  readonly onError?: (error: HttpError, request: HttpRequest) => HttpError;
}

/** `options.copyWith(path: …)` — новый запрос с другим адресом. */
export function requestWithPath(request: HttpRequest, path: string): HttpRequest {
  return { ...request, path };
}

/** `options.extra[key] = value`, но новым объектом. */
export function requestWithExtra(
  request: HttpRequest,
  key: string,
  value: unknown,
): HttpRequest {
  return { ...request, extra: { ...request.extra, [key]: value } };
}

/**
 * `RequestOptions.uri` — адрес вместе с query.
 *
 * Базового адреса у клиента нет: `path` к моменту отправки уже абсолютный,
 * его собирает `Endpoints` и доводит `PathInterceptor`.
 */
export function requestUri(request: HttpRequest): string {
  const query = encodeQuery(request.queryParameters);
  if (query.length === 0) return request.path;
  const separator = request.path.includes('?') ? '&' : '?';
  return `${request.path}${separator}${query}`;
}

/**
 * Кодирование `queryParameters`. `null`/`undefined` пропускаются (dio тоже
 * их не писал), список разворачивается в повторяющийся ключ.
 */
export function encodeQuery(parameters: HttpQueryParameters): string {
  return Object.entries(parameters)
    .flatMap(([key, value]) => encodeQueryPair(key, value))
    .join('&');
}

function encodeQueryPair(key: string, value: HttpQueryValue): readonly string[] {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return [encodeQueryEntry(key, value)];
  }
  return value.map((item) => encodeQueryEntry(key, item));
}

function encodeQueryEntry(key: string, value: string | number | boolean): string {
  return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
}

/**
 * Короткое описание причины сбоя — замена `DioException.message`.
 *
 * Dio складывал сюда многословный английский текст с советом поднять
 * таймаут; он попадал только в отладочный лог, поэтому перенесён смыслом,
 * а не буквой. Причины те же.
 */
const MESSAGE_BY_TYPE: Record<HttpErrorType, string> = {
  connectionTimeout: 'сервер не ответил за отведённое время',
  sendTimeout: 'тело запроса не ушло за отведённое время',
  receiveTimeout: 'ответ не дочитался за отведённое время',
  badCertificate: 'сертификат сервера отклонён',
  badResponse: 'сервер ответил кодом ошибки',
  cancel: 'запрос отменён',
  connectionError: 'соединение не состоялось',
  unknown: 'неизвестная ошибка транспорта',
};

export function httpErrorMessage(error: HttpError): string {
  const text = MESSAGE_BY_TYPE[error.type] ?? MESSAGE_BY_TYPE.unknown;
  const status = error.response?.status ?? error.statusCode;
  return status === undefined ? text : `${text} (${status})`;
}

/**
 * `data is Map` из Dart.
 *
 * Картой считается только обычный объект — такой, какой отдаёт
 * `JSON.parse`. Массив, `null`, `FormData`, `File` и прочие экземпляры
 * классов сюда не попадают: в Dart они `Map` тоже не были.
 */
export function asRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  if (typeof value !== 'object' || value === null) return null;
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return null;
  return value as Record<string, unknown>;
}
